import questionsConfig from '../config/questions.json';
import levelsConfig from '../config/levels.json';

export interface BaseQuestion {
  id: number;
  title: string;
  options: string[];
  explanation: string;
  difficulty: 'easy' | 'medium' | 'hard';
  score: number;
}

export interface SingleChoiceQuestion extends BaseQuestion {
  type: 'single';
  correct: number;
}

export interface MultipleChoiceQuestion extends BaseQuestion {
  type: 'multiple';
  correct: number[];
}

export type Question = SingleChoiceQuestion | MultipleChoiceQuestion;

export interface Level {
  id: number;
  name: string;
  description: string;
  requiredScore: number;
  questionCounts: {
    single: number;
    multiple: number;
  };
  questions: Question[];
}

// 随机打乱数组
const shuffleArray = <T>(array: T[]): T[] => {
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
};

// 类型守卫函数
function isSingleChoiceQuestion(question: any): question is SingleChoiceQuestion {
  return question.type === 'single' && typeof question.correct === 'number';
}

function isMultipleChoiceQuestion(question: any): question is MultipleChoiceQuestion {
  return question.type === 'multiple' && Array.isArray(question.correct);
}

// 根据题目类型获取随机题目
const getRandomQuestionsByType = (
  questions: any[],
  type: 'single' | 'multiple',
  count: number
): Question[] => {
  const typeQuestions = questions.filter(q => 
    type === 'single' ? isSingleChoiceQuestion(q) : isMultipleChoiceQuestion(q)
  );
  return shuffleArray(typeQuestions).slice(0, count);
};

// 修改关卡配置组织方式
export const organizeLevels = (): Level[] => {
  const allQuestions = questionsConfig.questions as any[];
  
  return levelsConfig.levels.map(level => {
    // 先获取所有题目
    const singleQuestions = getRandomQuestionsByType(
      allQuestions,
      'single',
      level.questionCounts.single
    );
    const multipleQuestions = getRandomQuestionsByType(
      allQuestions,
      'multiple',
      level.questionCounts.multiple
    );
    
    // 按照类型顺序组合题目：单选 -> 多选
    return {
      ...level,
      questions: [...singleQuestions, ...multipleQuestions]
    };
  });
};

// 获取所有关卡
export const levels = organizeLevels();

// 获取特定关卡的题目
export const getLevelQuestions = (levelId: number): Question[] => {
  const level = levels.find(l => l.id === levelId);
  return level?.questions || [];
};

// 检查答案是否正确
export const checkAnswer = (question: Question, answer: number | number[]): boolean => {
  if (question.type === 'single') {
    return answer === question.correct;
  } else {
    const selectedAnswers = answer as number[];
    return (
      selectedAnswers.length === question.correct.length &&
      selectedAnswers.every(a => question.correct.includes(a)) &&
      question.correct.every(c => selectedAnswers.includes(c))
    );
  }
}; 